import { useState } from 'react';

import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { legendClasses } from '@mui/x-charts/ChartsLegend';
import {
  differenceInCalendarDays,
  format,
  isSameDay,
  startOfDay,
  subDays,
} from 'date-fns';
import { Calendar, Info } from 'lucide-react';

import { SegmentedToggleButtonGroup } from '@modules/_shared/components/SegmentedToggleButtonGroup';
import { DAYS_PRESETS } from '@modules/_shared/shared/utils/dateRangeShortcuts';
import { VisibilityDateRangePopover } from '@modules/visibility-dashboard/components/VisibilityDateRangePopover';
import { useLocale } from '@shared/hooks/locale';

import type { Granularity } from './chartUtils';

// Shared tooltip typography for all activity charts — keep cells and the
// axis-value header at 14px so the tooltip doesn't dominate the card.
export const CHART_TOOLTIP_SX = {
  '& .MuiChartsTooltip-cell': { fontSize: '0.875rem' },
  '& .MuiChartsTooltip-axisValueCell': { fontSize: '0.875rem' },
} as const;

// Shared legend config for the built-in chart legends so they match the
// impressions card's custom legend: bottom-left, 12px labels, small square
// marks. `position` is consumed by the chart wrapper (BarChart/LineChart).
export const CHART_LEGEND_SLOT_PROPS = {
  direction: 'horizontal' as const,
  position: { vertical: 'bottom' as const, horizontal: 'start' as const },
  sx: {
    gap: 1.5,
    [`& .${legendClasses.mark}`]: {
      width: 10,
      height: 10,
      borderRadius: '2px',
    },
    [`& .${legendClasses.label}`]: {
      fontSize: '0.75rem',
      color: 'text.secondary',
    },
  },
};

// Small "?" affordance that explains a term on hover/tap.
export const InfoTip = ({
  title,
  size = 13,
  color = 'text.disabled',
  hoverColor = 'text.secondary',
}: {
  title: React.ReactNode;
  size?: number;
  color?: string;
  hoverColor?: string;
}) => (
  <Tooltip title={title} arrow enterTouchDelay={0} placement="top">
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        color,
        cursor: 'help',
        '&:hover': { color: hoverColor },
      }}
    >
      <Info size={size} />
    </Box>
  </Tooltip>
);

// Friendly empty state for a chart with no data in the selected range. Fills
// roughly the chart's height so the card keeps a stable size, with a soft icon
// chip above the message.
export const ChartEmptyState = ({
  icon,
  message,
  height = 260,
}: {
  icon: React.ReactNode;
  message: string;
  height?: number;
}) => (
  <Box
    sx={{
      height,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1.25,
      textAlign: 'center',
      px: 2,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'action.hover',
        color: 'text.disabled',
      }}
    >
      {icon}
    </Box>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {message}
    </Typography>
  </Box>
);

export const GranularityToggle = ({
  value,
  onChange,
}: {
  value: Granularity;
  onChange: (value: Granularity) => void;
}) => {
  const { t } = useLocale();
  return (
    <SegmentedToggleButtonGroup
      value={value}
      onChange={onChange}
      options={[
        { value: 'day', label: t('Daily') },
        { value: 'week', label: t('Weekly') },
      ]}
      sx={{
        '& .MuiToggleButton-root': {
          px: 1,
          py: 0.25,
          fontSize: '0.75rem',
          textTransform: 'none',
        },
      }}
    />
  );
};

// Per-chart controls row: granularity toggle on the left, a calendar popover on
// the right. Used at the top of each expanded funnel drill-down so the date
// range and bucketing apply directly to the chart shown underneath.
export const ChartRangeControls = ({
  start,
  end,
  granularity,
  onGranularity,
  onRange,
}: {
  start: Date;
  end: Date;
  granularity: Granularity;
  onGranularity: (value: Granularity) => void;
  onRange: (start: Date, end: Date) => void;
}) => {
  const { t, dateLocale } = useLocale();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const today = startOfDay(new Date());
  const days = differenceInCalendarDays(end, start) + 1;
  const isPreset =
    isSameDay(end, today) && (DAYS_PRESETS as readonly number[]).includes(days);
  const label = isPreset
    ? t('Last {{count}} day', { count: days })
    : `${format(start, 'd MMM', { locale: dateLocale })} – ${format(
        end,
        'd MMM yyyy',
        { locale: dateLocale },
      )}`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <GranularityToggle value={granularity} onChange={onGranularity} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 500 }}
        >
          {label}
        </Typography>
        <IconButton
          size="small"
          onClick={e => setAnchor(e.currentTarget)}
          sx={{ color: 'text.secondary' }}
        >
          <Calendar size={14} />
        </IconButton>
        <VisibilityDateRangePopover
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          startDateObj={start}
          endDateObj={end}
          minDate={subDays(today, 400)}
          setRange={(from, to) => onRange(startOfDay(from), startOfDay(to))}
        />
      </Box>
    </Box>
  );
};
